import { useMemo, useState } from 'react';
import { Map as IMap } from 'immutable';

import ModalV2, { Button, ButtonKind, Footer } from '../../../shared/ModalV2';
import { RecordUpdateRow } from '../../../shared/RecordUpdateRow/RecordUpdateRow.tsx';
import { ToggleSwitch } from '../../../shared/ToggleSwitch/ToggleSwitch.tsx';

import { IapExportResult } from '../../../hooks/packs.ts';

import { UpdateStatus } from '../../../types/enums/updateStatus.enum.ts';

import styles from './PackExportSelectModal.module.scss';

type PackExportSetupProps = {
    productIds: string[];
    exportResults: IapExportResult[];
    onExport: (ids: string[]) => void;
    onClose: () => void;
};

export const PackExportSelectModal = (props: PackExportSetupProps) => {
    const { productIds, onClose, onExport, exportResults } = props;
    const productsToReExport = exportResults.reduce(
        (acc: [string, number][], { productId, error, skipped }) => {
            if (!error || skipped) {
                return acc;
            }

            const index = productIds.findIndex((p) => p === productId);

            acc.push([productId, index]);

            return acc;
        },
        [],
    );

    const [isExportEverything, setIsExportEverything] = useState(
        productsToReExport.length === 0,
    );
    const [selectedProducts, setSelectedProducts] = useState<
        IMap<string, number>
    >(IMap(productsToReExport));

    const productUpdateResultsIndex = useMemo(() => {
        return new Map(exportResults.map((r) => [r.productId, r]));
    }, [exportResults]);

    const toggleProductSelection = (productId: string) => () => {
        if (selectedProducts.has(productId)) {
            setSelectedProducts(selectedProducts.delete(productId));
        } else {
            const index = productIds.findIndex((p) => p === productId);
            setSelectedProducts(selectedProducts.set(productId, index));
        }
    };

    const onExportEverythingToggle = () => {
        setIsExportEverything((prevState) => {
            return !prevState;
        });
    };

    const onExportClick = () => {
        const skus = isExportEverything
            ? []
            : Array.from(selectedProducts.sort().keys());

        onExport(skus);
    };

    const getStatusByResult = (result?: IapExportResult): UpdateStatus => {
        if (!result) {
            return UpdateStatus.NotStarted;
        }

        if (result.error) {
            return UpdateStatus.Error;
        }

        if (!result.skipped) {
            return UpdateStatus.Updated;
        }

        return UpdateStatus.NotStarted;
    };

    return (
        <ModalV2 onClose={onClose} className={styles.packExportSelect}>
            <h1>Select SKUs to export</h1>
            <div className={styles.exportAllWrapper}>
                <ToggleSwitch
                    checked={isExportEverything}
                    onToggle={onExportEverythingToggle}
                    disabled={!!exportResults.length}
                />
                <div>Export all</div>
            </div>
            <div className={styles.skus}>
                {productIds.map((p) => {
                    const result = productUpdateResultsIndex.get(p);

                    return (
                        <RecordUpdateRow
                            key={p}
                            id={p}
                            text={p}
                            status={getStatusByResult(result)}
                            error={result?.error || ''}
                            isSelected={
                                isExportEverything || selectedProducts.has(p)
                            }
                            isDisabled={isExportEverything}
                            onCheckboxClick={toggleProductSelection(p)}
                        />
                    );
                })}
            </div>
            <Footer>
                <Button kind={ButtonKind.SECONDARY} onClick={onClose}>
                    Close
                </Button>
                <Button
                    kind={ButtonKind.PRIMARY}
                    onClick={onExportClick}
                    disabled={!isExportEverything && selectedProducts.size < 1}
                >
                    Export
                </Button>
            </Footer>
        </ModalV2>
    );
};
